import * as PIXI from "pixi.js";
// TEMP DIAGNOSTICS — the trails were invisible once; this says where they go.
import { baseDiag } from "../../utils/fsDiag";

/**
 * The multiplier reaching out to what it multiplies.
 *
 * ── Why this exists ──
 *
 * A multiplier used to fire `mul.distribute` in its own cell and, a beat later,
 * every coin on the board burst and showed a bigger number. Nothing connected
 * the two: the box did something, and then unrelated cells changed. The player
 * has to be shown WHERE the bigger number came from, and the only honest way to
 * do that is to draw the thing that carries it.
 *
 * So the box hits the coins. One trail per coin, all leaving together, and each
 * coin's upgrade burst fires the moment its own trail lands.
 *
 * ── They arrive together, on purpose ──
 *
 * Every trail is given the same duration regardless of how far it has to fly,
 * so a coin on the far reel is hit at the same instant as the one next door.
 * The multiplier applies to the whole board AT ONCE; staggered arrivals would
 * say it happens cell by cell, which is not what the payout does.
 *
 * That duration is the ANIMATOR's, never this module's. A trail is one effect
 * across the whole game — the multiplier's bolts and the free-spins level pips
 * are the same bolt — so nothing here is allowed to have an opinion about how
 * fast it flies.
 *
 * ── The animator is GameView's, not ours ──
 *
 * It draws into `golden_trail_container`, a layer GameView owns at the same
 * zIndex 20 as `pot_trail_container` and the bonus scene's
 * `level_trail_container`.
 *
 * That layer is what makes the trails VISIBLE, and getting it wrong is what hid
 * them: FXManager emits particles into the layer ITSELF — `emitter.init(
 * parentContainer)`, "no wrapper" — not into the travelling wrapper whose
 * zIndex we set. Drawn into the flat symbol overlay the particles landed at the
 * bottom of a container whose cells sit at zIndex 6, and since every one of
 * these trails runs along a single reel, each was drawn underneath the very
 * cells it connected.
 *
 * ── It can never block the feature ──
 *
 * Every failure — no animator, no geometry, no particle bundle — still calls
 * `onArrival` and resolves. The upgrade this announces matters more than the
 * announcement, and the feature holds the game's UI lock while it runs.
 */

// ── The bow: how far a trail bulges sideways at its midpoint ──────────────
//
// As a share of the flight's own length, so the curve reads the same whether it
// crosses one cell or five and needs no device scaling — a longer flight gets a
// proportionally wider arc, which is what keeps them all looking like one
// gesture.
//
// 0.18 was picked to be the mildest value that still separates two neighbouring
// trails on the same axis. Much more and it stops reading as a charge being
// thrown and starts reading as something lobbed, which is the reason the
// animator draws straight lines by default.
const TRAIL_BOW_RATIO = 0.18;

// A one-cell hop is short enough that a proportional bow is a few pixels and
// vanishes; a five-cell flight is long enough that it would swing wide. Both
// ends are held so every trail in a volley curves by a comparable amount on
// screen, measured against the CELL so it survives any board size.
const TRAIL_BOW_MIN_CELLS = 0.18;
const TRAIL_BOW_MAX_CELLS = 0.5;

/**
 * Fire one trail from every multiplier to every cell it upgrades.
 *
 * With two multipliers on the board each one fires its own set, so a coin is
 * hit twice and still bursts once — they do not multiply each other, and the
 * board should show both of them acting rather than electing one.
 *
 * @param   {Object}   p
 * @param   {Object}   p.animator   The shared LevelChargingAnimator built by
 *          GameView. Its `trailContainer` is the layer, and it is also the
 *          space every grid point is measured in.
 * @param   {Array}    p.sources    Cell controllers doing the multiplying.
 * @param   {Array}    p.origins    Firing points in GLOBAL space, for a source
 *          that is not a cell at all — the bonus room's level box multiplying
 *          the whole board at the end of the feature. Used INSTEAD of `sources`
 *          when given; the box is not on the grid and has no cell controller.
 * @param   {Array}    p.targets    `[key, controller]` pairs being multiplied.
 * @param   {Function} [p.onArrival] Called once per target, with its key, the
 *          moment the first trail reaches it. This is where the upgrade goes.
 * @param   {Function} [p.diag] Which trace to log in — the runner passes its
 *          own, so a volley fired in the bonus room reads as `[FS]`.
 * @returns {Promise<void>} resolves when every trail has landed.
 */
export const fireMultiplierTrails = async ({
  animator = null,
  sources = [],
  origins = [],
  targets = [],
  onArrival = null,
  diag = baseDiag,
} = {}) => {
  const arrived = new Set();
  // Whatever happens below, every target must be told. Held here so the early
  // returns and the per-trail callbacks share one guarantee.
  const land = (key) => {
    if (arrived.has(key)) return;
    arrived.add(key);
    onArrival?.(key);
  };
  const landAll = () => targets.forEach(([key]) => land(key));

  const layer = animator?.trailContainer;
  diag("golden trail volley", {
    animator: animator ? "yes" : "MISSING",
    layer: layer ? layer.name : "MISSING",
    z: layer ? layer.zIndex : "-",
    sources: sources.length,
    origins: origins.length,
    targets: targets.length,
    // ── The number the particle budget actually turns on ──
    //
    // Density is one constant (TRAIL_SPAWN_COUNT); the LOAD is that constant
    // times how many bolts share the frame, and only this caller can vary that.
    // `live` is the steady-state particle count for this volley on the same
    // formula the constant is documented with — count / frequency * life *
    // bolts. 1,080 was smooth, 3,240 stuttered. If a volley here reports a big
    // number AND looks rough, that pairing is the evidence to act on.
    bolts: (origins.length || sources.length) * targets.length,
    live: Math.round(
      (origins.length || sources.length) * targets.length * 6 * 22.5,
    ),
  });
  if (!layer || layer.destroyed || !targets.length) {
    landAll();
    return;
  }

  // ── One list of firing points, whatever they are ──
  //
  // A cell resolves its own point per bolt (see below); a fixed origin is
  // converted once, here, because there is no cell to ask. Both end up as
  // points in the layer's space, and the volley below does not care which is
  // which. `origins` WINS: a caller that names one is firing from something
  // that is not on the board.
  const points = origins.length
    ? origins
        .map((p) => (p ? layer.toLocal(new PIXI.Point(p.x, p.y)) : null))
        .filter(Boolean)
        .map((p) => ({ gridCenter: () => ({ x: p.x, y: p.y }) }))
    : sources;

  if (!points.length) {
    landAll();
    return;
  }

  // ── Only a CELL bows its trails ──
  //
  // `origins` is the bonus room's level box multiplying the whole board from
  // off-grid: its trails fan out across five reels in five different
  // directions, which is the case the animator's straight line was written for
  // and it already looks right. A cell multiplier is the opposite — it fires at
  // its own neighbours, and on a full reel every one of those trails runs the
  // same axis.
  //
  // So the curve is not a style, it is an answer to collinearity, and it is
  // applied where the collinearity is.
  const bowing = !origins.length;
  const cell = animator?.symbolHeight || 0;

  const flights = [];
  for (const source of points) {
    let bowIndex = 0;
    for (const [key, target] of targets) {
      // ── Resolved FRESH per bolt, never cached ──
      //
      // Pot Collection says it out loud for its own trails: a volley can outlive
      // an orientation or resize change, so every bolt asks the grid again
      // rather than trusting a point measured before the first one launched.
      const start = source?.gridCenter?.(layer);
      const end = target?.gridCenter?.(layer);
      if (!start || !end) {
        land(key);
        continue;
      }
      // fireTrail takes its START in container space and its END in GLOBAL
      // space — the shape its first callers happened to have their points in.
      // Both of ours are grid points in the layer, so the end goes back out to
      // global for the animator to bring straight back in. A wasted round trip,
      // and cheaper than changing an animator the bonus scene depends on too.
      // Alternating sign, so the trails leave in a fan — one left, one right,
      // one left — instead of stacking. Counted over the targets that actually
      // FLY, not over the raw list: a target skipped for missing geometry
      // above would otherwise put two neighbours on the same side.
      let bow = 0;
      if (bowing) {
        const dist = Math.hypot(end.x - start.x, end.y - start.y);
        const span = cell
          ? Math.min(
              Math.max(dist * TRAIL_BOW_RATIO, cell * TRAIL_BOW_MIN_CELLS),
              cell * TRAIL_BOW_MAX_CELLS,
            )
          : dist * TRAIL_BOW_RATIO;
        bow = bowIndex % 2 === 0 ? span : -span;
        bowIndex += 1;
      }

      flights.push(
        // ── No duration: EVERY trail in the game flies for the same time ──
        //
        // This used to ask for 320ms while the level pips took the animator's
        // default of 200. Same emitter, same textures, same ramp — but the
        // streak is a fixed slice of TIME, so a longer flight makes a shorter,
        // slower-looking dart. The two read as different effects when they are
        // one.
        //
        // Left unset rather than set to 200, so the number lives in exactly one
        // place (LevelChargingAnimator's DEFAULT_DURATION) and the two cannot
        // drift apart again.
        animator.fireTrail(start, layer.toGlobal(new PIXI.Point(end.x, end.y)), {
          bow,
          onArrival: () => land(key),
        }),
      );
    }
  }

  if (!flights.length) {
    landAll();
    return;
  }

  await Promise.all(flights);
  // A trail that was cancelled mid-flight resolves without arriving. The cell
  // it was carrying still has to change.
  landAll();
};

export default fireMultiplierTrails;

